import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import TreeView from "../../common/treeView";
import DocumentPreviewPane from "../../common/DocumentPreview/DocumentPreviewPane";
import { normalizeFileExtension } from "../../common/DocumentPreview/documentUrlUtils";
import ResizableDiv from "../../common/resizeablediv/resizeablediv";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { getUserData } from "../../../services/apiService";
import {
  fetchStaffLookup,
  staffToDropdownOptions,
} from "../../../services/lookupService";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import { useEpsfStudySite } from "./epsfLedgerUi";
import {
  archiveBinderDocument,
  cancelBinderUploadRequest,
  fetchBinderDocument,
  fetchBinderDocuments,
  fetchBinderSections,
  fetchBinderUploadRequests,
  moveBinderDocument,
  moveBinderSection,
  saveBinderCustomSection,
  saveBinderUploadRequest,
  uploadBinderDocument,
} from "./epsfService";
import EpsfFormFillModal from "./EpsfFormFillModal";
import "./EpsfPages.scss";

const UPLOAD_DOC_TYPES = [
  "Packing List",
  "Shipment / Courier Document",
  "Temperature Logger Evidence",
  "Calibration Certificate",
  "Destruction Certificate",
  "Sponsor / Depot Confirmation",
  "IMP Handling Instructions",
  "Pharmacy Manual",
  "Other Document",
];

const RELATED_TXN_ROUTES = {
  RECEIPT: "/epsf/imp/receipt",
  RECONCILIATION: "/epsf/imp/accountability",
  RECONCILE: "/epsf/imp/accountability",
  DESTRUCTION: "/epsf/imp/destruction",
  DESTROY: "/epsf/imp/destruction",
  RETURN: "/epsf/imp/returns",
  DISPENSE: "/epsf/imp/dispense",
  TEMPERATURE: "/epsf/imp/storage",
  STORAGE: "/epsf/imp/storage",
};

function formatUtcDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function sourceBadge(sourceType) {
  const src = String(sourceType || "").toUpperCase();
  const label = src === "ESOURCE_FORM" ? "FORM" : "UPLOAD";
  const tone = src === "ESOURCE_FORM" ? "form" : "upload";
  return (
    <span className={`epsf-binder-source epsf-binder-source--${tone}`}>
      {label}
    </span>
  );
}

function statusBadge(status) {
  const s = String(status || "FILED").toUpperCase();
  return (
    <span className={`epsf-binder-status epsf-binder-status--${s.toLowerCase()}`}>
      {s === "APPROVED" ? "Approved" : s === "ARCHIVED" ? "Archived" : "Filed"}
    </span>
  );
}

function sectionIdOf(s) {
  return s?.BinderSectionId || s?.binderSectionId || "";
}

function parentIdOf(s) {
  return s?.ParentSectionId || s?.parentSectionId || null;
}

function docIdOf(d) {
  return d?.BinderDocumentId || d?.binderDocumentId || "";
}

function docSectionIdOf(d) {
  return d?.BinderSectionId || d?.binderSectionId || "";
}

function requestIdOf(r) {
  return r?.UploadRequestId || r?.uploadRequestId || "";
}

function relatedRouteFor(detail) {
  const type = String(
    detail?.RelatedEntityType || detail?.relatedEntityType || "",
  )
    .trim()
    .toUpperCase();
  return RELATED_TXN_ROUTES[type] || null;
}

function relatedLabelFor(detail) {
  const type = String(
    detail?.RelatedEntityType || detail?.relatedEntityType || "",
  )
    .trim()
    .toUpperCase();
  if (!type) return "View related transaction";
  if (type === "RECEIPT") return "View receipt";
  if (type === "RECONCILIATION" || type === "RECONCILE") {
    return "View reconciliation";
  }
  if (type === "DESTRUCTION" || type === "DESTROY") return "View destruction";
  if (type === "RETURN") return "View return";
  if (type === "DISPENSE") return "View dispense";
  return `View ${type.toLowerCase()}`;
}

function staffLabel(staffMap, staffId) {
  if (!staffId) return "—";
  const hit = staffMap.get(String(staffId).toLowerCase());
  return hit || String(staffId);
}

function buildBinderTree(sections, documents, uploadRequests, staffMap) {
  const byParent = new Map();
  (sections || []).forEach((s) => {
    const pid = parentIdOf(s);
    const key = pid ? String(pid).toLowerCase() : "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(s);
  });

  const docsBySection = new Map();
  (documents || []).forEach((d) => {
    const sid = String(docSectionIdOf(d) || "").toLowerCase();
    if (!sid) return;
    if (!docsBySection.has(sid)) docsBySection.set(sid, []);
    docsBySection.get(sid).push(d);
  });

  const reqBySection = new Map();
  (uploadRequests || []).forEach((r) => {
    const sid = String(r.BinderSectionId || r.binderSectionId || "").toLowerCase();
    if (!sid) return;
    if (!reqBySection.has(sid)) reqBySection.set(sid, []);
    reqBySection.get(sid).push(r);
  });

  const sortSections = (list) =>
    (list || [])
      .slice()
      .sort(
        (a, b) =>
          (a.DisplayOrder ?? a.displayOrder ?? 0) -
            (b.DisplayOrder ?? b.displayOrder ?? 0) ||
          String(a.SectionCode || a.sectionCode || "").localeCompare(
            String(b.SectionCode || b.sectionCode || ""),
          ),
      );

  const sortDocs = (list) =>
    (list || [])
      .slice()
      .sort((a, b) => {
        const da = new Date(a.CreatedAtUtc || a.createdAtUtc || 0).getTime();
        const db = new Date(b.CreatedAtUtc || b.createdAtUtc || 0).getTime();
        return db - da;
      });

  const mapDocNode = (d) => {
    const id = docIdOf(d);
    const title = d.Title || d.title || d.OriginalFileName || "Evidence";
    const status = String(d.Status || d.status || "FILED").toUpperCase();
    const statusLabel =
      status === "APPROVED"
        ? "Approved"
        : status === "FILED"
          ? "Filed"
          : status;
    const ship = d.ShipmentReference || d.shipmentReference || "";
    const batch = d.BatchLotNumber || d.batchLotNumber || "";
    const dtype = d.DocumentType || d.documentType || "";
    const fname = d.OriginalFileName || d.originalFileName || "";
    return {
      id,
      name: `${title} · ${statusLabel}`,
      type: "file",
      binderDocument: d,
      searchHaystack: [
        title,
        dtype,
        ship,
        batch,
        fname,
        statusLabel,
        d.SourceType || d.sourceType,
      ]
        .filter(Boolean)
        .join(" "),
    };
  };

  const mapRequestNode = (r) => {
    const id = requestIdOf(r);
    const title = r.Title || r.title || "Upload request";
    const assignee = staffLabel(
      staffMap,
      r.AssignedToStaffId || r.assignedToStaffId,
    );
    return {
      id: `req:${id}`,
      name: `${title} — assigned to ${assignee}`,
      type: "file",
      isUploadRequest: true,
      hideMenu: true,
      uploadRequest: r,
      searchHaystack: [
        title,
        r.DocumentType || r.documentType,
        assignee,
      ]
        .filter(Boolean)
        .join(" "),
    };
  };

  const mapFolder = (section) => {
    const id = sectionIdOf(section);
    const isSystem = Boolean(section.IsSystem ?? section.isSystem ?? true);
    const childSections = sortSections(
      byParent.get(String(id).toLowerCase()) || [],
    );
    const childFolders = childSections.map(mapFolder);
    const sectionDocs = sortDocs(
      docsBySection.get(String(id).toLowerCase()) || [],
    ).map(mapDocNode);
    const sectionReqs = (reqBySection.get(String(id).toLowerCase()) || []).map(
      mapRequestNode,
    );

    let children = [...childFolders, ...sectionReqs, ...sectionDocs];
    if (!children.length) {
      children = [
        {
          id: `${id}__empty`,
          name: "No evidence filed in this folder.",
          type: "file",
          isEmptyMarker: true,
          hideMenu: true,
        },
      ];
    }

    return {
      id,
      name: section.SectionName || section.sectionName || "Folder",
      type: "folder",
      isSystem,
      canMove: !isSystem,
      parentId: parentIdOf(section) || null,
      sectionCode: section.SectionCode || section.sectionCode || "",
      section,
      children,
    };
  };

  return sortSections(byParent.get("__root__") || []).map(mapFolder);
}

export default function EpsfBinder() {
  const navigate = useNavigate();
  const { loading, canView, moduleAccess, canEvidence, isSystemAdmin } =
    useEpsfAccess();
  const allow =
    canView && moduleAccess && (isSystemAdmin || canEvidence);
  const ctx = useEpsfStudySite();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const user = getUserData() || {};

  const [sections, setSections] = useState([]);
  const [docs, setDocs] = useState([]);
  const [uploadRequests, setUploadRequests] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [listBusy, setListBusy] = useState(false);
  const [selectedKind, setSelectedKind] = useState(null); // folder | document | request
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showMoveFolder, setShowMoveFolder] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [addFolderParentId, setAddFolderParentId] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadForm, setUploadForm] = useState({
    title: "",
    documentType: UPLOAD_DOC_TYPES[0],
    binderSectionId: "",
    file: null,
    shipmentReference: "",
    batchLotNumber: "",
    descriptionNotes: "",
    uploadRequestId: "",
  });
  const [formPreviewInstanceId, setFormPreviewInstanceId] = useState("");
  const [requestForm, setRequestForm] = useState({
    title: "",
    documentType: UPLOAD_DOC_TYPES[0],
    assignedToStaffId: "",
    notes: "",
  });

  const canManage = isSystemAdmin || canEvidence;

  const staffMap = useMemo(() => {
    const map = new Map();
    (staffOptions || []).forEach((opt) => {
      const id = String(opt.value || "").toLowerCase();
      if (id) map.set(id, opt.label);
    });
    return map;
  }, [staffOptions]);

  const loadSections = useCallback(async () => {
    try {
      const res = await fetchBinderSections();
      if (res?.success) setSections(res.rows || []);
      else setSections([]);
    } catch {
      setSections([]);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setDocs([]);
      setUploadRequests([]);
      return;
    }
    setListBusy(true);
    try {
      const [docRes, reqRes] = await Promise.all([
        fetchBinderDocuments({
          StudyId: ctx.studyId,
          SiteId: ctx.siteId,
          BinderSectionId: null,
          SearchText: null,
          FilterSourceType: null,
        }),
        fetchBinderUploadRequests({
          StudyId: ctx.studyId,
          SiteId: ctx.siteId,
        }),
      ]);
      setDocs(docRes?.success ? docRes.rows || [] : []);
      setUploadRequests(reqRes?.success ? reqRes.rows || [] : []);
    } catch {
      setDocs([]);
      setUploadRequests([]);
    } finally {
      setListBusy(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchStaffLookup(user);
        if (cancelled) return;
        setStaffOptions(staffToDropdownOptions(list || []));
      } catch {
        if (!cancelled) setStaffOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.OrganizationId, user?.StaffId]);

  const treeData = useMemo(
    () => buildBinderTree(sections, docs, uploadRequests, staffMap),
    [sections, docs, uploadRequests, staffMap],
  );

  const leafSections = useMemo(
    () => (sections || []).filter((s) => parentIdOf(s)),
    [sections],
  );

  const refreshAll = async () => {
    await Promise.all([loadSections(), loadDocuments()]);
  };

  const openDetail = async (row) => {
    const id = docIdOf(row);
    if (!id) return;
    setSelectedKind("document");
    setSelectedFolder(null);
    setSelectedRequest(null);
    setSelectedNodeId(id);
    setDetailBusy(true);
    try {
      const res = await fetchBinderDocument(id, ctx.studyId, ctx.siteId);
      setDetail(res?.success ? res.rows?.[0] || row : row);
    } catch {
      setDetail(row);
    } finally {
      setDetailBusy(false);
    }
  };

  const openFolder = (node) => {
    setSelectedKind("folder");
    setSelectedFolder(node);
    setDetail(null);
    setSelectedRequest(null);
    setSelectedNodeId(node.id);
  };

  const openRequest = (node) => {
    const req = node.uploadRequest;
    setSelectedKind("request");
    setSelectedRequest(req);
    setSelectedFolder(null);
    setDetail(null);
    setSelectedNodeId(node.id);
  };

  const onArchive = () => {
    if (!detail) return;
    const id = docIdOf(detail);
    showAlert({
      title: "Archive evidence?",
      message:
        "Archiving keeps the audit trail. The document will no longer appear in the active binder.",
      type: "warning",
      buttonText: "Archive",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const res = await archiveBinderDocument(id, ctx.studyId, ctx.siteId);
        if (!res?.success) {
          showAlert({
            title: "Archive failed",
            message: res?.message || "Could not archive document.",
            type: "danger",
          });
          return;
        }
        setDetail(null);
        setSelectedKind(null);
        setSelectedNodeId(null);
        loadDocuments();
      },
    });
  };

  const openUploadModal = (sectionId, uploadRequestId = "", requestRow = null) => {
    const req = requestRow || selectedRequest;
    const fromRequest = Boolean(uploadRequestId && req);
    setUploadForm({
      title: fromRequest ? req.Title || req.title || "" : "",
      documentType: fromRequest
        ? req.DocumentType || req.documentType || UPLOAD_DOC_TYPES[0]
        : UPLOAD_DOC_TYPES[0],
      binderSectionId: fromRequest
        ? req.BinderSectionId || req.binderSectionId || sectionId || ""
        : sectionId || "",
      file: null,
      shipmentReference: "",
      batchLotNumber: "",
      descriptionNotes: "",
      uploadRequestId: uploadRequestId || "",
    });
    setShowUpload(true);
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (uploadBusy) return;
    if (!uploadForm.file || !uploadForm.title.trim() || !uploadForm.binderSectionId) {
      showAlert({
        title: "Missing fields",
        message: "File, title and binder section are required.",
        type: "warning",
      });
      return;
    }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("OrganizationId", user.OrganizationId || user.organizationId || "");
      fd.append("StaffId", user.StaffId || user.staffId || "");
      fd.append("StudyId", ctx.studyId || "");
      fd.append("SiteId", ctx.siteId || "");
      fd.append("BinderSectionId", uploadForm.binderSectionId);
      fd.append("Title", uploadForm.title.trim());
      fd.append("DocumentType", uploadForm.documentType);
      fd.append("ShipmentReference", uploadForm.shipmentReference || "");
      fd.append("BatchLotNumber", uploadForm.batchLotNumber || "");
      fd.append("DescriptionNotes", uploadForm.descriptionNotes || "");
      if (uploadForm.uploadRequestId) {
        fd.append("UploadRequestId", uploadForm.uploadRequestId);
      }
      fd.append("file", uploadForm.file);
      const res = await uploadBinderDocument(fd);
      if (!res?.success) {
        showAlert({
          title: "Upload failed",
          message: res?.message || "Could not file document.",
          type: "danger",
        });
        return;
      }
      setShowUpload(false);
      setSelectedRequest(null);
      await loadDocuments();
    } catch (err) {
      showAlert({
        title: "Upload failed",
        message: err?.message || "Could not file document.",
        type: "danger",
      });
    } finally {
      setUploadBusy(false);
    }
  };

  const submitAddFolder = async (e) => {
    e.preventDefault();
    const parentId = addFolderParentId || selectedFolder?.id;
    if (folderBusy || !parentId || !newFolderName.trim()) return;
    setFolderBusy(true);
    try {
      const res = await saveBinderCustomSection({
        ParentSectionId: parentId,
        SectionName: newFolderName.trim(),
      });
      if (!res?.success) {
        showAlert({
          title: "Could not create folder",
          message: res?.message || "Folder was not created.",
          type: "danger",
        });
        return;
      }
      setShowAddFolder(false);
      setNewFolderName("");
      setAddFolderParentId("");
      await refreshAll();
    } finally {
      setFolderBusy(false);
    }
  };

  const submitMoveFolder = async (e) => {
    e.preventDefault();
    if (folderBusy || !selectedFolder?.id || !moveTargetId) return;
    if (selectedFolder.isSystem) {
      showAlert({
        title: "Protected folder",
        message: "System taxonomy folders cannot be moved.",
        type: "warning",
      });
      return;
    }
    setFolderBusy(true);
    try {
      const res = await moveBinderSection({
        BinderSectionId: selectedFolder.id,
        TargetSectionId: moveTargetId,
      });
      if (!res?.success) {
        showAlert({
          title: "Move failed",
          message: res?.message || "Could not move folder.",
          type: "danger",
        });
        return;
      }
      setShowMoveFolder(false);
      setMoveTargetId("");
      await refreshAll();
    } finally {
      setFolderBusy(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (requestBusy || !selectedFolder?.id) return;
    if (
      !requestForm.title.trim() ||
      !requestForm.assignedToStaffId ||
      !requestForm.documentType
    ) {
      showAlert({
        title: "Missing fields",
        message: "Title, document type and assignee are required.",
        type: "warning",
      });
      return;
    }
    setRequestBusy(true);
    try {
      const res = await saveBinderUploadRequest({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        BinderSectionId: selectedFolder.id,
        Title: requestForm.title.trim(),
        DocumentType: requestForm.documentType,
        AssignedToStaffId: requestForm.assignedToStaffId,
        DescriptionNotes: requestForm.notes || null,
      });
      if (!res?.success) {
        showAlert({
          title: "Could not create request",
          message: res?.message || "Upload request was not created.",
          type: "danger",
        });
        return;
      }
      setShowRequest(false);
      setRequestForm({
        title: "",
        documentType: UPLOAD_DOC_TYPES[0],
        assignedToStaffId: "",
        notes: "",
      });
      await loadDocuments();
    } finally {
      setRequestBusy(false);
    }
  };

  const onCancelRequest = () => {
    if (!selectedRequest) return;
    const id = requestIdOf(selectedRequest);
    showAlert({
      title: "Cancel upload request?",
      message: "The placeholder will be removed from the binder tree.",
      type: "warning",
      buttonText: "Cancel request",
      cancelText: "Keep",
      onConfirm: async () => {
        hideAlert();
        const res = await cancelBinderUploadRequest({
          UploadRequestId: id,
          StudyId: ctx.studyId,
          SiteId: ctx.siteId,
        });
        if (!res?.success) {
          showAlert({
            title: "Cancel failed",
            message: res?.message || "Could not cancel request.",
            type: "danger",
          });
          return;
        }
        setSelectedRequest(null);
        setSelectedKind(null);
        loadDocuments();
      },
    });
  };

  const handleTreeSelect = (node) => {
    if (!node || node.isEmptyMarker) return;
    if (node.type === "folder") {
      openFolder(node);
      return;
    }
    if (node.isUploadRequest) {
      openRequest(node);
      return;
    }
    if (node.binderDocument) {
      openDetail(node.binderDocument);
      return;
    }
    openDetail({ BinderDocumentId: node.id, Title: node.name });
  };

  const handleTreeAction = async (payload, action) => {
    if (!canManage && action !== "moveNode") return;

    if (action === "newFile" && payload?.type === "folder") {
      openFolder(payload);
      openUploadModal(payload.id);
      return;
    }

    if (action === "subFolder" && payload?.type === "folder") {
      openFolder(payload);
      setAddFolderParentId(payload.id);
      setNewFolderName("");
      setShowAddFolder(true);
      return;
    }

    if (action === "parentFolder" && payload?.type === "folder") {
      const parentId = payload.parentId;
      if (!parentId) {
        showAlert({
          title: "Cannot add at parent level",
          message:
            "This is a top-level taxonomy folder. Use Add sub folder instead.",
          type: "warning",
        });
        return;
      }
      openFolder(payload);
      setAddFolderParentId(parentId);
      setNewFolderName("");
      setShowAddFolder(true);
      return;
    }

    if (action === "moveFolder" && payload?.type === "folder") {
      if (payload.isSystem || payload.canMove === false) {
        showAlert({
          title: "Protected folder",
          message: "System taxonomy folders cannot be moved.",
          type: "warning",
        });
        return;
      }
      openFolder(payload);
      setMoveTargetId("");
      setShowMoveFolder(true);
      return;
    }

    if (action === "requestUpload" && payload?.type === "folder") {
      openFolder(payload);
      setRequestForm({
        title: "",
        documentType: UPLOAD_DOC_TYPES[0],
        assignedToStaffId: "",
        notes: "",
      });
      setShowRequest(true);
      return;
    }

    if (action !== "moveNode") return;
    if (!canManage) return;

    const source = payload?.sourceNode;
    const dest = payload?.destinationNode;
    if (!source || !dest || dest.type !== "folder") return;

    if (source.type === "folder") {
      if (source.isSystem || source.canMove === false) {
        showAlert({
          title: "Protected folder",
          message: "System taxonomy folders cannot be moved.",
          type: "warning",
        });
        return;
      }
      const res = await moveBinderSection({
        BinderSectionId: source.id,
        TargetSectionId: dest.id,
      });
      if (!res?.success) {
        showAlert({
          title: "Move failed",
          message: res?.message || "Could not move folder.",
          type: "danger",
        });
        return;
      }
      await refreshAll();
      return;
    }

    if (source.isUploadRequest || source.isEmptyMarker) return;

    const docId = source.binderDocument
      ? docIdOf(source.binderDocument)
      : source.id;
    const res = await moveBinderDocument({
      BinderDocumentId: docId,
      TargetSectionId: dest.id,
      StudyId: ctx.studyId,
      SiteId: ctx.siteId,
    });
    if (!res?.success) {
      showAlert({
        title: "Move failed",
        message: res?.message || "Could not move document.",
        type: "danger",
      });
      return;
    }
    await loadDocuments();
  };

  const relatedPath = relatedRouteFor(detail);
  const blobPath = detail?.BlobPath || detail?.blobPath || "";
  const formInstanceId = detail?.FormInstanceId || detail?.formInstanceId;
  const previewExt = normalizeFileExtension(
    (detail?.OriginalFileName || detail?.originalFileName || "")
      .split(".")
      .pop(),
  );

  const renderRightPanel = () => {
    if (!ctx.studyId || !ctx.siteId) {
      return (
        <div className="epsf-binder-empty-study">
          Select study and site in the breadcrumb bar to open the binder.
        </div>
      );
    }

    if (!selectedKind) {
      return (
        <div className="epsf-binder-right-empty">
          <p>
            Select a folder to upload evidence, create an upload request, or add
            a custom subfolder. Select a document to preview details.
          </p>
          <p className="text-muted mb-0">
            Drag documents (or custom folders) onto another folder to move them.
            System taxonomy folders stay protected.
          </p>
        </div>
      );
    }

    if (selectedKind === "folder" && selectedFolder) {
      return (
        <div className="epsf-binder-right-folder">
          <div className="epsf-binder-right-head">
            <div>
              <div className="epsf-binder-right-kicker">Folder</div>
              <h3>{selectedFolder.name}</h3>
              {selectedFolder.isSystem ? (
                <span className="epsf-binder-pill">System taxonomy</span>
              ) : (
                <span className="epsf-binder-pill epsf-binder-pill--custom">
                  Custom folder
                </span>
              )}
            </div>
          </div>
          {canManage ? (
            <div className="epsf-binder-right-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => openUploadModal(selectedFolder.id)}
              >
                <i className="fas fa-upload me-1" />
                Upload document
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => {
                  setRequestForm({
                    title: "",
                    documentType: UPLOAD_DOC_TYPES[0],
                    assignedToStaffId: "",
                    notes: "",
                  });
                  setShowRequest(true);
                }}
              >
                <i className="fas fa-user-clock me-1" />
                Request upload
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setAddFolderParentId(selectedFolder.id);
                  setNewFolderName("");
                  setShowAddFolder(true);
                }}
              >
                <i className="fas fa-folder-plus me-1" />
                Add subfolder
              </button>
              {!selectedFolder.isSystem ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setMoveTargetId("");
                    setShowMoveFolder(true);
                  }}
                >
                  <i className="fas fa-arrows-alt me-1" />
                  Move folder
                </button>
              ) : null}
            </div>
          ) : null}
          <p className="epsf-binder-right-hint">
            Upload files into this folder, or create a placeholder request
            assigned to a staff member so they can upload the document later.
          </p>
        </div>
      );
    }

    if (selectedKind === "request" && selectedRequest) {
      const assignee = staffLabel(
        staffMap,
        selectedRequest.AssignedToStaffId || selectedRequest.assignedToStaffId,
      );
      return (
        <div className="epsf-binder-right-request">
          <div className="epsf-binder-right-head">
            <div>
              <div className="epsf-binder-right-kicker">Upload request</div>
              <h3>
                {selectedRequest.Title || selectedRequest.title || "Request"}
              </h3>
            </div>
          </div>
          <dl className="epsf-binder-detail-grid">
            <div>
              <dt>Assigned to</dt>
              <dd>{assignee}</dd>
            </div>
            <div>
              <dt>Document type</dt>
              <dd>
                {selectedRequest.DocumentType ||
                  selectedRequest.documentType ||
                  "—"}
              </dd>
            </div>
            <div>
              <dt>Folder</dt>
              <dd>
                {selectedRequest.SectionName ||
                  selectedRequest.sectionName ||
                  "—"}
              </dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>
                {formatUtcDate(
                  selectedRequest.CreatedAtUtc || selectedRequest.createdAtUtc,
                )}
              </dd>
            </div>
            {(selectedRequest.Notes || selectedRequest.notes) && (
              <div>
                <dt>Notes</dt>
                <dd>{selectedRequest.Notes || selectedRequest.notes}</dd>
              </div>
            )}
          </dl>
          {canManage ? (
            <div className="epsf-binder-right-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  openUploadModal(
                    selectedRequest.BinderSectionId ||
                      selectedRequest.binderSectionId,
                    requestIdOf(selectedRequest),
                    selectedRequest,
                  )
                }
              >
                <i className="fas fa-upload me-1" />
                Fulfil upload
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onCancelRequest}
              >
                Cancel request
              </button>
            </div>
          ) : null}
        </div>
      );
    }

    if (selectedKind === "document") {
      return (
        <div className="epsf-binder-right-doc">
          <div className="epsf-binder-right-head">
            <div>
              <div className="epsf-binder-right-kicker">Document</div>
              <h3>{detail?.Title || detail?.title || "Evidence"}</h3>
            </div>
            <div className="epsf-binder-right-actions">
              {formInstanceId ? (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setFormPreviewInstanceId(String(formInstanceId))
                  }
                >
                  View completed form
                </button>
              ) : null}
              {relatedPath ? (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigate(relatedPath)}
                >
                  {relatedLabelFor(detail)}
                </button>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onArchive}
                >
                  Archive
                </button>
              ) : null}
            </div>
          </div>

          {detailBusy ? (
            <p className="text-muted">Loading…</p>
          ) : (
            <dl className="epsf-binder-detail-grid mb-3">
              <div>
                <dt>Status</dt>
                <dd>{statusBadge(detail?.Status || detail?.status)}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>
                  {sourceBadge(detail?.SourceType || detail?.sourceType)}
                </dd>
              </div>
              <div>
                <dt>Document type</dt>
                <dd>{detail?.DocumentType || detail?.documentType || "—"}</dd>
              </div>
              <div>
                <dt>Section</dt>
                <dd>{detail?.SectionName || detail?.sectionName || "—"}</dd>
              </div>
              <div>
                <dt>Filed</dt>
                <dd>
                  {formatUtcDate(detail?.CreatedAtUtc || detail?.createdAtUtc)}
                </dd>
              </div>
              <div>
                <dt>Batch / Lot</dt>
                <dd>
                  {detail?.BatchLotNumber || detail?.batchLotNumber || "—"}
                </dd>
              </div>
              <div>
                <dt>Shipment / reference</dt>
                <dd>
                  {detail?.ShipmentReference ||
                    detail?.shipmentReference ||
                    "—"}
                </dd>
              </div>
              <div>
                <dt>Related operation</dt>
                <dd>
                  {detail?.RelatedEntityType ||
                    detail?.relatedEntityType ||
                    "—"}
                </dd>
              </div>
            </dl>
          )}

          {blobPath ? (
            <div className="epsf-binder-preview">
              <DocumentPreviewPane
                active
                sourceModule="EPSF"
                sourceTable="Epsf_BinderDocument"
                documentId={String(docIdOf(detail) || "")}
                documentUrl={blobPath}
                documentExtension={previewExt}
                documentTitle={
                  detail?.OriginalFileName ||
                  detail?.originalFileName ||
                  detail?.Title ||
                  detail?.title ||
                  "Evidence"
                }
                showDownloadOriginal
              />
            </div>
          ) : formInstanceId ? (
            <div className="epsf-binder-right-empty">
              <p className="mb-2">
                This evidence is linked to a completed eSource form. Open the
                form here to review the recorded answers.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  setFormPreviewInstanceId(String(formInstanceId))
                }
              >
                Open completed form
              </button>
            </div>
          ) : (
            <div className="epsf-binder-right-empty">
              <p className="mb-0">No file preview is available for this item.</p>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page epsf-binder-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Binder" },
          ]}
          actions={[
            { icon: "fas fa-refresh", label: "Refresh", onClick: refreshAll },
            ...(canManage
              ? [
                  {
                    icon: "fas fa-upload",
                    label: "Upload document",
                    onClick: () =>
                      openUploadModal(
                        selectedFolder?.id ||
                          (leafSections[0]
                            ? sectionIdOf(leafSections[0])
                            : ""),
                      ),
                  },
                ]
              : []),
          ]}
        />

        <div className="epsf-binder-split">
            <ResizableDiv
              direction="horizontal"
              initialSize={28}
              minSize={18}
              maxSize={42}
              usePercentage
              storageKey="epsf-binder-tree-width"
            >
              <div className="epsf-binder-left-panel">
                {!ctx.studyId || !ctx.siteId ? (
                  <div className="epsf-binder-empty-study">
                    Select study and site to load the binder tree.
                  </div>
                ) : listBusy && !treeData.length ? (
                  <p className="text-muted mb-0 p-2">Loading binder…</p>
                ) : (
                  <TreeView
                    data={treeData}
                    onSelect={handleTreeSelect}
                    onAction={handleTreeAction}
                    selectedNodeId={selectedNodeId}
                    canMove={canManage}
                    treeToolBar
                    showAddFolder={false}
                    showFolderMenus={canManage}
                    showFileMenus={false}
                    showFolderNewFile={false}
                    folderMenuVariant="binder"
                  />
                )}
              </div>
            </ResizableDiv>
            <div className="epsf-binder-right-panel">
              <div className="epsf-binder-right-box">{renderRightPanel()}</div>
            </div>
          </div>

        {showUpload
          ? createPortal(
              <div className="modal fade show d-block epsf-modal" role="dialog">
                <div className="modal-dialog">
                  <form className="modal-content" onSubmit={submitUpload}>
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {uploadForm.uploadRequestId
                          ? "Fulfil upload request"
                          : "Upload document"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        disabled={uploadBusy}
                        onClick={() => setShowUpload(false)}
                      />
                    </div>
                    <div className="modal-body">
                      <div className="epsf-form-field">
                        <label className="form-label">
                          File <span className="mandatory">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control"
                          disabled={uploadBusy}
                          onChange={(e) =>
                            setUploadForm((prev) => ({
                              ...prev,
                              file: e.target.files?.[0] || null,
                              title:
                                prev.title ||
                                e.target.files?.[0]?.name?.replace(
                                  /\.[^.]+$/,
                                  "",
                                ) ||
                                "",
                            }))
                          }
                        />
                      </div>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Title <span className="mandatory">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={uploadForm.title}
                          disabled={uploadBusy}
                          onChange={(e) =>
                            setUploadForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Binder section <span className="mandatory">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={uploadForm.binderSectionId}
                          disabled={uploadBusy || !!uploadForm.uploadRequestId}
                          onChange={(e) =>
                            setUploadForm((prev) => ({
                              ...prev,
                              binderSectionId: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select section</option>
                          {(sections || []).map((s) => {
                            const id = sectionIdOf(s);
                            const parentName =
                              s.ParentSectionName || s.parentSectionName || "";
                            const name = s.SectionName || s.sectionName;
                            return (
                              <option key={id} value={id}>
                                {parentName ? `${parentName} › ${name}` : name}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Document type <span className="mandatory">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={uploadForm.documentType}
                          disabled={uploadBusy}
                          onChange={(e) =>
                            setUploadForm((prev) => ({
                              ...prev,
                              documentType: e.target.value,
                            }))
                          }
                        >
                          {UPLOAD_DOC_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="row g-2">
                        <div className="col-6">
                          <div className="epsf-form-field">
                            <label className="form-label">
                              Shipment / reference
                            </label>
                            <input
                              className="form-control"
                              value={uploadForm.shipmentReference}
                              disabled={uploadBusy}
                              onChange={(e) =>
                                setUploadForm((prev) => ({
                                  ...prev,
                                  shipmentReference: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="epsf-form-field">
                            <label className="form-label">Batch / Lot</label>
                            <input
                              className="form-control"
                              value={uploadForm.batchLotNumber}
                              disabled={uploadBusy}
                              onChange={(e) =>
                                setUploadForm((prev) => ({
                                  ...prev,
                                  batchLotNumber: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="epsf-form-field mb-0">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={uploadForm.descriptionNotes}
                          disabled={uploadBusy}
                          onChange={(e) =>
                            setUploadForm((prev) => ({
                              ...prev,
                              descriptionNotes: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={uploadBusy}
                        onClick={() => setShowUpload(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={uploadBusy}
                      >
                        {uploadBusy ? "Filing…" : "File document"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body,
            )
          : null}

        {showAddFolder
          ? createPortal(
              <div className="modal fade show d-block epsf-modal" role="dialog">
                <div className="modal-dialog">
                  <form className="modal-content" onSubmit={submitAddFolder}>
                    <div className="modal-header">
                      <h5 className="modal-title">Add folder</h5>
                      <button
                        type="button"
                        className="btn-close"
                        disabled={folderBusy}
                        onClick={() => {
                          setShowAddFolder(false);
                          setAddFolderParentId("");
                        }}
                      />
                    </div>
                    <div className="modal-body">
                      <p className="small text-muted">
                        Parent:{" "}
                        <strong>
                          {(sections || []).find(
                            (s) =>
                              String(sectionIdOf(s)).toLowerCase() ===
                              String(
                                addFolderParentId || selectedFolder?.id || "",
                              ).toLowerCase(),
                          )?.SectionName ||
                            selectedFolder?.name ||
                            "Folder"}
                        </strong>
                      </p>
                      <div className="epsf-form-field mb-0">
                        <label className="form-label">
                          Folder name <span className="mandatory">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={newFolderName}
                          disabled={folderBusy}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={folderBusy}
                        onClick={() => {
                          setShowAddFolder(false);
                          setAddFolderParentId("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={folderBusy || !newFolderName.trim()}
                      >
                        {folderBusy ? "Creating…" : "Create folder"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body,
            )
          : null}

        {showMoveFolder
          ? createPortal(
              <div className="modal fade show d-block epsf-modal" role="dialog">
                <div className="modal-dialog">
                  <form className="modal-content" onSubmit={submitMoveFolder}>
                    <div className="modal-header">
                      <h5 className="modal-title">Move folder</h5>
                      <button
                        type="button"
                        className="btn-close"
                        disabled={folderBusy}
                        onClick={() => {
                          setShowMoveFolder(false);
                          setMoveTargetId("");
                        }}
                      />
                    </div>
                    <div className="modal-body">
                      <p className="small text-muted">
                        Moving: <strong>{selectedFolder?.name}</strong>
                      </p>
                      <div className="epsf-form-field mb-0">
                        <label className="form-label">
                          Destination folder <span className="mandatory">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={moveTargetId}
                          disabled={folderBusy}
                          onChange={(e) => setMoveTargetId(e.target.value)}
                        >
                          <option value="">Select destination</option>
                          {(sections || [])
                            .filter((s) => {
                              const id = sectionIdOf(s);
                              if (!id || !selectedFolder?.id) return false;
                              if (
                                String(id).toLowerCase() ===
                                String(selectedFolder.id).toLowerCase()
                              ) {
                                return false;
                              }
                              return true;
                            })
                            .map((s) => {
                              const id = sectionIdOf(s);
                              const parentName =
                                s.ParentSectionName || s.parentSectionName || "";
                              const name = s.SectionName || s.sectionName;
                              return (
                                <option key={id} value={id}>
                                  {parentName
                                    ? `${parentName} › ${name}`
                                    : name}
                                </option>
                              );
                            })}
                        </select>
                      </div>
                      <p className="small text-muted mt-2 mb-0">
                        Tip: you can also drag a custom folder onto another
                        folder in the tree.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={folderBusy}
                        onClick={() => {
                          setShowMoveFolder(false);
                          setMoveTargetId("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={folderBusy || !moveTargetId}
                      >
                        {folderBusy ? "Moving…" : "Move folder"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body,
            )
          : null}

        {showRequest
          ? createPortal(
              <div className="modal fade show d-block epsf-modal" role="dialog">
                <div className="modal-dialog">
                  <form className="modal-content" onSubmit={submitRequest}>
                    <div className="modal-header">
                      <h5 className="modal-title">Request upload</h5>
                      <button
                        type="button"
                        className="btn-close"
                        disabled={requestBusy}
                        onClick={() => setShowRequest(false)}
                      />
                    </div>
                    <div className="modal-body">
                      <p className="small text-muted">
                        Folder: <strong>{selectedFolder?.name}</strong>
                      </p>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Title <span className="mandatory">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={requestForm.title}
                          disabled={requestBusy}
                          onChange={(e) =>
                            setRequestForm((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Document type <span className="mandatory">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={requestForm.documentType}
                          disabled={requestBusy}
                          onChange={(e) =>
                            setRequestForm((prev) => ({
                              ...prev,
                              documentType: e.target.value,
                            }))
                          }
                        >
                          {UPLOAD_DOC_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="epsf-form-field">
                        <label className="form-label">
                          Assign to <span className="mandatory">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={requestForm.assignedToStaffId}
                          disabled={requestBusy}
                          onChange={(e) =>
                            setRequestForm((prev) => ({
                              ...prev,
                              assignedToStaffId: e.target.value,
                            }))
                          }
                        >
                          <option value="">Select staff</option>
                          {staffOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="epsf-form-field mb-0">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={requestForm.notes}
                          disabled={requestBusy}
                          onChange={(e) =>
                            setRequestForm((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={requestBusy}
                        onClick={() => setShowRequest(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={requestBusy}
                      >
                        {requestBusy ? "Saving…" : "Create request"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>,
              document.body,
            )
          : null}

        <EpsfFormFillModal
          open={Boolean(formPreviewInstanceId)}
          formInstanceId={formPreviewInstanceId || ""}
          title="Completed form"
          mode="view"
          onClose={() => setFormPreviewInstanceId("")}
        />

        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    </EpsfAccessGate>
  );
}
